import React, { Component } from 'react';
import { connect } from 'react-redux';
import Router from 'next/router';
import { parseCookies } from '@/lib/cookies';
import classNames from 'classnames';
import Link from 'next/link';
import { publish } from '@/utils/publish';

import styles from './Login.module.scss';

import Logo from '@/components/Helpers/Logo';
import queryParams from '@/utils/queryParams';
import Notification from '@/components/Base/Notification';
import FormValidator from '@/components/Forms/Validator';
import Submit from '@/components/Forms/Submit';
import Links from '@/components/session/Links';
import Loader from '@/components/Helpers/Loader';
import { config } from '@/config';
import { setStateAsync } from '@/utils/helpers';
import TwoFactorInput from '@/components/Helpers/TwoFactorInput';
import { getEnv } from '@/lib/env';

import { loginUser, loginSsoUser } from '@/store/actions/session.actions';
import { fetchCustomDomainBranding } from '@/store/actions/customDomains.actions';
import CF360Login from '@/components/Login/CF360Login';

import { auth0 } from '@/lib/auth0';

// Branding is cosmetic, so it never gets to block the form for longer than this.
const BRANDING_TIMEOUT_MS = 2500;

class Login extends Component {
  static async getInitialProps({ ctx }) {
    const { token, domain } = parseCookies(ctx);
    //
    const isClientflow360 = config().CLIENTFLOW360 || domain === 'production_clientflow360';

    // REQUEST-75: when the CRM is served from a verified custom domain the marketing-site
    // login can't be used — its auth cookie is scoped to the tenant domain and never
    // reaches the custom domain. Render the platform login here instead (same flow
    // AgentFlow360 custom domains use); /v1/sessions runs the CF360 validations.
    const requestHost = (ctx?.req?.headers?.host || (typeof window !== 'undefined' ? window.location.host : '')).split(
      ':'
    )[0];
    const mainHost = (getEnv('NEXT_PUBLIC_WEBPAGE_HOST') || '').split(':')[0];
    const isCustomDomainHost = !!requestHost && !!mainHost && !requestHost.endsWith(mainHost);

    if (isClientflow360 && !isCustomDomainHost) {
      const cf360LoginUrl = `${getEnv('NEXT_PUBLIC_CF360_LANDING')}/`;
      if (ctx.res) {
        ctx.res.writeHead(302, { Location: cf360LoginUrl });
        ctx.res.end();
      } else {
        window.location = cf360LoginUrl;
      }
      return {};
    }

    if (token) {
      if (ctx.res) {
        ctx.res.statusCode = 302;
        ctx.res.setHeader('Location', '/');
      } else {
        Router.push('/');
      }
    } else {
      try {
        if (config().SSO_FEATURE) {
          const { idToken } = (await auth0.getSession(ctx.req, ctx.res)) || {};
          return { sso_token: idToken, loading: !!idToken, isClientflow360 };
        } else {
          return { isClientflow360 };
        }
      } catch (err) {
        return { err: err, isClientflow360 };
      }
    }
  }

  state = {
    formLogin: {
      login: '',
      password: '',
      remember: false,
      otp_secret: '',
      otp_attempt: ''
    },
    twoFactorAuthForm: {
      otp_secret: '',
      otp_attempt: '',
      save_this_device: false
    },
    passwordVisible: false,
    branding: null,
    twoFactorRequired: false,
    otpAttemptVisible: false,
    type: '',
    loading: true
  };

  setStateAsync = setStateAsync.bind(this);

  async componentDidMount() {
    const login = queryParams('login');
    if (config().SSO_FEATURE) {
      if (this.props.sso_token) {
        await this.setStateAsync({ loading: this.props.loading });
        await this.props
          .loginSsoUser({ sso_token: this.props.sso_token })
          .then(val => {
            if (val && val.otp_secret && val.last_otp_at) {
              let otp_secret = val.otp_secret;
              let last_otp_at = val.last_otp_at;
              this.setState({
                twoFactorRequired: true,
                loading: false,
                formLogin: { otp_secret, otp_attempt: '', last_otp_at }
              });
            } else {
              this.setState({ loading: true, type: val.two_fa_type });
            }
          })
          .catch(res => {
            if (Object.keys(res?.jsonResponse?.errors || {}).includes('sso_token')) {
              window.location = `/auth/logout?returnTo=${getEnv('NEXT_PUBLIC_APP_URL')}/sso-logout`;
            }
          });
      } else {
        this.setState({ loading: true });
        window.location = '/auth/login';
      }
    } else {
      if (this.props.isClientflow360 || config().CLIENTFLOW360) await this.loadBranding();
      this.setState({ loading: false });
    }
    if (login) {
      this.setState({ formLogin: { login } });
    }
    history.pushState(null, document.title, location.href);
    window.addEventListener('popstate', function () {
      history.pushState(null, document.title, location.href);
    });
  }

  get logInForm() {
    const {
      formLogin: { login, password, remember }
    } = this.state;

    if (config().SSO_FEATURE) {
      return null;
    }

    return (
      <form name="formLogin">
        <div className="field">
          <label className={classNames('label', styles.label)}>Username</label>
          <div className={classNames('control has-icons-right', styles.control)}>
            <input
              name="login"
              onChange={this.validateOnChange}
              className={classNames('input', {
                'is-danger': this.hasError('formLogin', 'login', 'required')
              })}
              type="text"
              placeholder="Enter username"
              data-validate='["required"]'
              value={login}
            />
            <span className={classNames('icon is-small is-right', styles.icon)}>
              <i className="fas fa-user" />
            </span>
          </div>
          {this.hasError('formLogin', 'login', 'required') && (
            <span className={classNames('help is-danger', styles.help)}>Field is required</span>
          )}
        </div>
        <div className="field">
          <label className={classNames('label', styles.label)}>Password</label>
          <div className={classNames('control has-icons-right', styles.control)}>
            <input
              name="password"
              className={classNames('input', {
                'is-danger': this.hasError('formLogin', 'password', 'required')
              })}
              type={this.state.passwordVisible ? 'Text' : 'Password'}
              placeholder="Password"
              onChange={this.validateOnChange}
              data-validate='["required"]'
              value={password}
            />
            <span className={classNames('icon is-small is-right', styles.passwordVisible, styles.icon)}>
              <i
                onClick={() => this.setState({ passwordVisible: !this.state.passwordVisible })}
                className={this.state.passwordVisible ? 'fas fa-eye' : 'fas fa-eye-slash'}
              />
            </span>
            {this.hasError('formLogin', 'password', 'required') && (
              <span className={classNames('help is-danger', styles.help)}>Field is required</span>
            )}
          </div>

          {/* <div className={styles.checkboxWrapper}>
            <div className="field">
              <input
                id="remember"
                type="checkbox"
                name="remember"
                className="is-checkradio is-medium"
                onChange={this.validateOnChange}
                checked={remember}
              />
              <label htmlFor="remember">Remember me</label>
            </div>
          </div> */}
          <Link href="/reset-password">
            <p className={classNames(styles.link, 'mt-2')}>Forgot your password?</p>
          </Link>
        </div>
        <Submit label="Login" className="button is-medium is-fullwidth is-success" onSubmit={this.onSubmit} />
      </form>
    );
  }

  get labelText() {
    switch (this.state.type) {
      case 'application':
        return 'Please enter 6-digit code from the two-factor app on your device';
      case 'sms':
        return 'Please enter 6-digit code from the message on your mobile device';
      case 'email':
        return 'Please enter 6-digit code from your e-mail';
      default:
        return 'Please enter 6-digit 2fa code';
    }
  }

  get twoFactorAuthForm() {
    const {
      formLogin: { login, password, otp_secret, otp_attempt, save_this_device }
    } = this.state;

    return (
      <form name="formLogin">
        <div className="field">
          <div className={classNames('control has-icons-right', styles.control)}>
            <input
              name="login"
              className={classNames('input')}
              type="hidden"
              invalid={`${this.hasError('formLogin', 'login', 'required')}`}
              data-validate='["required"]'
              value={login}
            />
          </div>
        </div>
        <TwoFactorInput
          length={6}
          inputName="otp_attempt"
          helpLabel={this.labelText}
          onChange={this.validateOnChange}
        />
        {/* <div className="field">
          <label className={classNames('label', styles.label)}>{this.labelText}</label>
          <div className={classNames('control has-icons-right', styles.control)}>
            <input
              name="otp_attempt"
              className={classNames('input', {
                'is-danger': this.hasError('formLogin', 'otp_attempt', 'required')
              })}
              type={this.state.otpAttemptVisible ? 'Text' : 'Password'}
              placeholder="Code"
              invalid={`${this.hasError('formLogin', 'otp_attempt', 'required')}`}
              onChange={this.validateOnChange}
              data-validate='["required"]'
              value={otp_attempt}
            />
            <span className={classNames('icon is-small is-right', styles.otpAttemptVisible, styles.icon)}>
              <i
                onClick={() => this.setState({ otpAttemptVisible: !this.state.otpAttemptVisible })}
                className={this.state.otpAttemptVisible ? 'fas fa-eye' : 'fas fa-eye-slash'}
              ></i>
            </span>
            {this.hasError('formLogin', 'otp_attempt', 'required') && (
              <span className={classNames('help is-danger', styles.help)}>Field is required</span>
            )}
          </div>
        </div> */}
        <div className={styles.checkboxWrapper}>
          <div className="field">
            <input
              id="save_this_device"
              type="checkbox"
              name="save_this_device"
              className="is-checkradio is-medium"
              onChange={this.validateOnChange}
              checked={save_this_device}
            />
            <label htmlFor="save_this_device">Save this device</label>
          </div>
        </div>
        <Submit label="Verify" className="button is-medium is-fullwidth is-success" onSubmit={this.onSubmit} />
      </form>
    );
  }

  // Agency branding for the ClientWave360 login page. Cosmetic by design: a host that isn't
  // a verified custom domain, or a failed lookup, leaves the ClientWave360 mark in place and
  // must never block the form.
  loadBranding = async () => {
    try {
      // Capped so a slow or unreachable API can never hold the login form behind the loader;
      // the page just renders with the default ClientWave360 brand instead.
      const branding = await Promise.race([
        this.props.fetchCustomDomainBranding(),
        new Promise(resolve => setTimeout(() => resolve(null), BRANDING_TIMEOUT_MS))
      ]);
      if (branding?.is_custom_domain) this.setState({ branding });
    } catch (err) {
      // Intentionally ignored — the login form renders with the default brand.
    }
  };

  validateOnChange = (event, values = null) => {
    const input = event.target;
    const form = input.form;
    let value;
    if (values) {
      value = values;
    } else {
      value = input.type === 'checkbox' ? input.checked : input.value;
    }

    const result = FormValidator.validate(input);
    this.setState({
      [form.name]: {
        ...this.state[form.name],
        [input.name]: value == input.checked ? value : value.split(/\s/).join(''),
        errors: {
          ...this.state[form.name].errors,
          [input.name]: result
        }
      }
    });
  };

  hasError = (formName, inputName, method) => {
    return (
      this.state[formName] &&
      this.state[formName].errors &&
      this.state[formName].errors[inputName] &&
      this.state[formName].errors[inputName][method]
    );
  };

  onSubmit = async (e, callback) => {
    e.preventDefault();
    const form = e.target.form;
    const inputs = [...form.elements].filter(i => ['INPUT', 'SELECT'].includes(i.nodeName));
    const { errors, hasError } = FormValidator.bulkValidate(inputs);

    this.setState({
      [form.name]: {
        ...this.state[form.name],
        errors
      }
    });

    const { login, password, remember, otp_secret, otp_attempt, save_this_device } = this.state[form.name];

    if (this.props.sso_token) {
      await this.props
        .loginSsoUser({ sso_token: this.props.sso_token, otp_secret, otp_attempt, save_this_device })
        .then(val => {
          if (val && val.otp_secret && val.last_otp_at) {
            let otp_secret = val.otp_secret;
            this.setState({
              formLogin: { sso_token: this.props.sso_token, otp_secret, otp_attempt: '', save_this_device }
            });
            this.setState({ twoFactorRequired: true, loading: false });
          }
        })
        .catch(res => publish('error', res));
    } else {
      if (hasError) {
        return callback();
      }
      await this.props
        .loginUser({ login, password, remember, otp_secret, otp_attempt, save_this_device })
        .then(val => {
          if (val && val.otp_secret && val.last_otp_at) {
            let otp_secret = val.otp_secret;
            this.setState({ formLogin: { login, password, remember, otp_secret, otp_attempt: '', save_this_device } });
            this.setState({ twoFactorRequired: true, type: val.two_fa_type });
          }
        })
        .catch(res => {
          publish('error', res);
          if (Object.keys(res?.jsonResponse?.errors || {}).includes('sso_token')) {
            window.location = `/auth/logout?returnTo=${getEnv('NEXT_PUBLIC_APP_URL')}/sso-logout`;
          }
        });
    }
    callback();
  };

  render() {
    if (this.state.loading) return <Loader loading />;

    // ClientWave360 gets the branded, email-only login; every other tenant keeps the
    // original card below untouched. isClientflow360 comes from getInitialProps, which also
    // honors the `domain` cookie used to preview this tenant outside production.
    if (this.props.isClientflow360 || config().CLIENTFLOW360) {
      const {
        formLogin: { login, password },
        branding,
        passwordVisible,
        twoFactorRequired
      } = this.state;

      return (
        <>
          <CF360Login
            branding={branding}
            login={login}
            password={password}
            passwordVisible={passwordVisible}
            onChange={this.validateOnChange}
            onSubmit={this.onSubmit}
            onTogglePassword={() => this.setState({ passwordVisible: !passwordVisible })}
            hasError={field => this.hasError('formLogin', field, 'required')}
            twoFactorForm={twoFactorRequired ? this.twoFactorAuthForm : null}
          />
          <Notification classNames={'login'} />
        </>
      );
    }

    return (
      <>
        <div className={styles.loginWrapper}>
          <div className={classNames('card', styles.card)}>
            {!config().IS_DEMO && (
              <div className={styles.imageWrapper}>
                <Logo />
              </div>
            )}
            {this.state.twoFactorRequired ? this.twoFactorAuthForm : this.logInForm}
            <Links />
          </div>
          {!config().REMOVED_COPYRIGHT_LINE && (
            <div className={styles.privacyInfo}>©{new Date().getFullYear()} - Market Access Direct</div>
          )}
        </div>
        <Notification classNames={'login'} />
      </>
    );
  }
}

const mapStateToProps = () => ({});

export default connect(mapStateToProps, { loginUser, loginSsoUser, fetchCustomDomainBranding })(Login);
